import type { Layer, Lane, WorkflowArchitecture } from './types';

export const ARCH_SEED: Array<{ code: Layer['code']; name: string; note: string; stack_position: number }> = [
  { code: 'experience', name: 'Experience', note: 'the point of contact', stack_position: 1 },
  { code: 'flow', name: 'Flow', note: 'how work moves', stack_position: 2 },
  { code: 'logic', name: 'Logic', note: 'what the system decides', stack_position: 3 },
  { code: 'data', name: 'Data', note: 'sources of truth', stack_position: 4 },
  { code: 'integration', name: 'Integration', note: 'machine-to-machine', stack_position: 5 },
];

export const LANE_SEED: Array<{ code: Lane['code']; name: string; note: string }> = [
  { code: 'governance', name: 'Governance', note: 'who owns & directs' },
  { code: 'controls', name: 'Controls', note: 'what catches failure' },
  { code: 'people', name: 'People', note: 'can the humans perform' },
];

export const LAYER_DEFINITIONS: Record<string, string> = {
  experience: 'The surface where humans and external systems touch the workflow — UX, interfaces, information visibility, external interaction.',
  flow: 'How work moves through the process — sequence, handoffs, routing, ownership of a step, waiting, human decisions.',
  logic: 'Rules, thresholds, decisions, and calculations the system applies (or should).',
  data: 'The entity model, sources of truth, quality, lineage, and persistent state.',
  integration: 'Machine-to-machine exchange — APIs, ETL, feeds, connectors, orchestration jobs, event pipelines.',
  foundation: 'Substrate: capacity, performance, hosting, security posture, AI compute. Opt-in per engagement.',
  governance: 'Ownership, direction, roadmap, escalation, funding, roles, standards, cross-team structure.',
  controls: 'Preventive, detective, and corrective checks against failure. Regulatory alignment and audit trails.',
  people: 'Skills, training, adoption, capacity, key-person dependencies — whether humans can perform their role.',
};

const VALID = new Set([
  ...ARCH_SEED.map((l) => l.code),
  'foundation',
  ...LANE_SEED.map((l) => l.code),
]);

const DIAG_RE = /(\d+)\s+of\s+(\d+)\s+findings\s+sit\s+(?:in|across)\s+([^—\-]+)/i;

function emptyLayer(seed: (typeof ARCH_SEED)[number]): Layer {
  return {
    code: seed.code,
    name: seed.name,
    stack_position: seed.stack_position,
    primary_count: 0,
    secondary_count: 0,
    pct_primary: 0,
    rationale: '',
    findings: [],
    themes: [],
  };
}

function emptyLane(seed: (typeof LANE_SEED)[number]): Lane {
  return {
    code: seed.code,
    name: seed.name,
    primary_count: 0,
    tag_count: 0,
    pct_primary: 0,
    rationale: '',
    findings: [],
    themes: [],
  };
}

function namedRows(seg: string, rows: Array<Layer | Lane>): Array<Layer | Lane> {
  const s = seg.toLowerCase();
  return rows.filter((r) => s.includes(r.code) || s.includes((r.name || '').toLowerCase()));
}

function hasInvalidLayerNames(seg: string): boolean {
  const skip = new Set([
    'and', 'or', 'the', 'layers', 'layer', 'lanes', 'lane', 'findings', 'finding',
    'sit', 'in', 'across', 'of', 'this', 'isnt', 'its', 'problem', 'alone', 'end',
    'operating', 'model',
  ]);
  const tokens = (seg.toLowerCase().match(/[a-z][a-z0-9_]+/g) || []).filter((t) => !skip.has(t) && t.length > 2);
  if (!tokens.length) return false;
  const names = new Set([...VALID, ...ARCH_SEED.map((l) => l.name.toLowerCase()), ...LANE_SEED.map((l) => l.name.toLowerCase()), 'foundation']);
  return tokens.some((t) => !names.has(t));
}

function rewriteDiagnostic(diag: string, rows: Array<Layer | Lane>, total: number): string {
  const hot = rows.filter((r) => r.hot && r.primary_count > 0);
  const top = (hot.length ? hot : rows.filter((r) => r.primary_count > 0).sort((a, b) => b.primary_count - a.primary_count).slice(0, 3));
  const label = (r: Layer | Lane) => r.name || r.code;
  const fallbackNames = top.map(label).join(' + ') || 'the classified layers';
  const fallbackN = top.reduce((a, r) => a + r.primary_count, 0) || total;

  const text = diag || '';
  const split = text.split(/\s+[—\-]\s+/, 2);
  const rest = split.length === 2 ? split[1].trim() : '';
  const m = DIAG_RE.exec(text);
  const named = m ? namedRows(m[3], rows) : [];
  const invalid = hasInvalidLayerNames(m ? m[3] : text);

  let head: string;
  if (named.length && !invalid) {
    const n = named.reduce((a, r) => a + r.primary_count, 0);
    head = `${n} of ${total} findings sit in ${named.map(label).join(' + ')}`;
  } else {
    head = `${fallbackN} of ${total} findings sit in ${fallbackNames}`;
  }
  if (rest) return `${head} — ${rest}`;
  return `${head} — this isn't a technology problem alone, it's an end-to-end operating model problem.`;
}

/** Pad the 5+3 taxonomy and rewrite a diagnostic that leaked entity-type tags. */
export function normalizeWorkflowArchitecture(wa: WorkflowArchitecture): WorkflowArchitecture {
  const byArch = new Map((wa.architecture || []).map((l) => [l.code, l]));
  const byLane = new Map((wa.oversight || []).map((l) => [l.code, l]));
  const hasFoundation = byArch.has('foundation') && ((byArch.get('foundation')?.primary_count || 0) > 0
    || (byArch.get('foundation')?.secondary_count || 0) > 0);

  const architecture: Layer[] = ARCH_SEED.map((seed) => {
    const existing = byArch.get(seed.code);
    return existing ? { ...emptyLayer(seed), ...existing, name: existing.name || seed.name, stack_position: existing.stack_position || seed.stack_position } : emptyLayer(seed);
  });
  if (hasFoundation) {
    const f = byArch.get('foundation')!;
    architecture.push({
      ...f,
      name: f.name || 'Foundation',
      stack_position: f.stack_position || 6,
      rationale: f.rationale || '',
      findings: f.findings || [],
      themes: f.themes || [],
    });
  }

  const oversight: Lane[] = LANE_SEED.map((seed) => {
    const existing = byLane.get(seed.code);
    return existing
      ? { ...emptyLane(seed), ...existing, name: existing.name || seed.name }
      : emptyLane(seed);
  });

  const rows = [...architecture, ...oversight];
  const total = rows.reduce((a, r) => a + (r.primary_count || 0), 0);
  const diagnostic = rewriteDiagnostic(wa.diagnostic || '', rows, total);

  return {
    ...wa,
    architecture,
    oversight,
    total_findings: total,
    diagnostic,
  };
}
